/**
 * Costi e tariffe — pagina di configurazione economica.
 *
 * Due blocchi:
 *  - le tariffe orarie per reparto, una riga per ogni valore di
 *    `interventions.tipo_intervento` ('Meccanico', 'Carrozziere')
 *  - i due parametri di listino (ricarico ricambi, soglia di margine minimo)
 *
 * La rotta e' gated da `costs.manage_rates`, quindi chi arriva qui vede tutto.
 * La colonna "Costo orario" resta comunque condizionata alla presenza del campo
 * nella risposta: e' il backend a decidere cosa mandare, e la pagina non deve
 * dare per scontato di riceverlo.
 */
import { useState } from 'react'
import {
  Card, Table, InputNumber, Button, Switch, Typography, Space, Row, Col,
  message, Tag, Alert, theme,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type {
  TariffaRepartoFull, TariffaRepartoUpdate,
  ParametriListino, ParametriListinoUpdate,
} from '@/types/tariffe'

const { Text, Title } = Typography

const CostiTariffePage = () => {
  const { token } = theme.useToken()
  const queryClient = useQueryClient()

  const { data: tariffe, isLoading } = useQuery<TariffaRepartoFull[]>({
    queryKey: ['tariffe'],
    queryFn: async () => (await axiosInstance.get(API_ENDPOINTS.TARIFFE)).data,
  })

  const { data: parametri } = useQuery<ParametriListino>({
    queryKey: ['parametri-listino'],
    queryFn: async () => (await axiosInstance.get(API_ENDPOINTS.PARAMETRI_LISTINO)).data,
  })

  // Modifiche in sospeso, per riga: si salva una riga alla volta con il suo
  // pulsante, così una tariffa sbagliata non trascina con sé anche l'altra.
  const [bozza, setBozza] = useState<Record<number, TariffaRepartoUpdate>>({})
  const [bozzaParam, setBozzaParam] = useState<ParametriListinoUpdate>({})

  const salvaTariffa = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: TariffaRepartoUpdate }) =>
      (await axiosInstance.put(API_ENDPOINTS.TARIFFA(id), data)).data,
    onSuccess: (_d, v) => {
      message.success('Tariffa aggiornata')
      setBozza(b => { const n = { ...b }; delete n[v.id]; return n })
      queryClient.invalidateQueries({ queryKey: ['tariffe'] })
    },
    onError: () => message.error('Salvataggio non riuscito'),
  })

  const salvaParametri = useMutation({
    mutationFn: async (data: ParametriListinoUpdate) =>
      (await axiosInstance.put(API_ENDPOINTS.PARAMETRI_LISTINO, data)).data,
    onSuccess: () => {
      message.success('Parametri aggiornati')
      setBozzaParam({})
      queryClient.invalidateQueries({ queryKey: ['parametri-listino'] })
    },
    onError: () => message.error('Salvataggio non riuscito'),
  })

  const valore = (r: TariffaRepartoFull, campo: keyof TariffaRepartoUpdate) =>
    bozza[r.id]?.[campo] ?? (r as never)[campo]

  const modifica = (id: number, campo: keyof TariffaRepartoUpdate, v: unknown) =>
    setBozza(b => ({ ...b, [id]: { ...b[id], [campo]: v } }))

  // Il costo orario si mostra solo se il backend l'ha mandato: senza
  // costs.manage_rates la chiave non esiste proprio nella risposta.
  const mostraCosto = (tariffe ?? []).some(t => t.costo_orario !== undefined)

  const colonne = [
    {
      title: 'Reparto',
      dataIndex: 'reparto',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    ...(mostraCosto ? [{
      title: 'Costo orario (€/h)',
      dataIndex: 'costo_orario',
      width: 190,
      render: (_v: unknown, r: TariffaRepartoFull) => (
        <InputNumber
          min={0}
          precision={2}
          step={1}
          value={Number(valore(r, 'costo_orario'))}
          onChange={v => modifica(r.id, 'costo_orario', v)}
          style={{ width: '100%' }}
        />
      ),
    }] : []),
    {
      title: 'Tariffa esposta (€/h)',
      dataIndex: 'tariffa_esposta',
      width: 200,
      render: (_v: unknown, r: TariffaRepartoFull) => (
        <InputNumber
          min={0}
          precision={2}
          step={1}
          value={Number(valore(r, 'tariffa_esposta'))}
          onChange={v => modifica(r.id, 'tariffa_esposta', v)}
          style={{ width: '100%' }}
        />
      ),
    },
    ...(mostraCosto ? [{
      title: 'Margine',
      width: 110,
      render: (_v: unknown, r: TariffaRepartoFull) => {
        const costo = Number(valore(r, 'costo_orario'))
        const esposta = Number(valore(r, 'tariffa_esposta'))
        if (!esposta) return <Text type="secondary">—</Text>
        const pct = ((esposta - costo) / esposta) * 100
        return <Tag color={pct <= 0 ? 'red' : 'green'}>{pct.toFixed(0)}%</Tag>
      },
    }] : []),
    {
      title: 'Attiva',
      dataIndex: 'attivo',
      width: 90,
      render: (_v: unknown, r: TariffaRepartoFull) => (
        <Switch
          size="small"
          checked={Boolean(valore(r, 'attivo'))}
          onChange={v => modifica(r.id, 'attivo', v)}
        />
      ),
    },
    {
      title: '',
      width: 110,
      render: (_v: unknown, r: TariffaRepartoFull) => (
        <Button
          size="small"
          type="primary"
          icon={<SaveOutlined />}
          disabled={!bozza[r.id]}
          loading={salvaTariffa.isPending}
          onClick={() => salvaTariffa.mutate({ id: r.id, data: bozza[r.id] })}
        >
          Salva
        </Button>
      ),
    },
  ]

  const paramValore = (campo: keyof ParametriListino) =>
    campo in bozzaParam ? bozzaParam[campo] : parametri?.[campo] ?? null

  return (
    <div style={{ padding: 16, maxWidth: 1000 }}>
      <Title level={4} style={{ marginTop: 0 }}>Costi e tariffe</Title>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Il reparto è la chiave della tariffa"
        description={
          'Ogni riga corrisponde a un valore del campo "Reparto" dell\'intervento. '
          + 'Un intervento il cui reparto non ha una tariffa attiva non può essere quotato: '
          + 'il calcolo si ferma con un errore, non applica zero.'
        }
      />

      <Card size="small" title="Tariffe orarie per reparto" style={{ marginBottom: 16 }}>
        <Table<TariffaRepartoFull>
          dataSource={tariffe ?? []}
          columns={colonne}
          rowKey="id"
          size="small"
          loading={isLoading}
          pagination={false}
        />
        {mostraCosto && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Il costo orario è un dato interno: lo vede solo chi ha il permesso
            «Gestisce costi e tariffe orarie».
          </Text>
        )}
      </Card>

      <Card size="small" title="Parametri di listino">
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={10}>
            <Text type="secondary" style={{ fontSize: 12 }}>Ricarico ricambi di default (%)</Text>
            <InputNumber
              min={0}
              precision={2}
              step={1}
              style={{ width: '100%' }}
              value={paramValore('ricarico_ricambi_default') as number | null}
              onChange={v => setBozzaParam(b => ({ ...b, ricarico_ricambi_default: v }))}
            />
          </Col>
          <Col xs={24} sm={10}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Soglia di margine minimo (%) — vuoto = controllo disattivato
            </Text>
            <InputNumber
              min={0}
              precision={2}
              step={1}
              placeholder="disattivata"
              style={{ width: '100%' }}
              value={paramValore('soglia_margine_minimo') as number | null}
              onChange={v => setBozzaParam(b => ({ ...b, soglia_margine_minimo: v }))}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              disabled={Object.keys(bozzaParam).length === 0}
              loading={salvaParametri.isPending}
              onClick={() => salvaParametri.mutate(bozzaParam)}
              style={{ width: '100%' }}
            >
              Salva
            </Button>
          </Col>
        </Row>
        <Space size={16} style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12, color: token.colorTextTertiary }}>
            Valori salvati: ricarico {parametri?.ricarico_ricambi_default ?? '—'}%
            {' · '}
            soglia {parametri?.soglia_margine_minimo == null
              ? 'disattivata'
              : `${parametri.soglia_margine_minimo}%`}
          </Text>
        </Space>
      </Card>
    </div>
  )
}

export default CostiTariffePage
